import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { NavLink } from "react-router-dom";

const css = () => ({
  headerChevron: {
    color: "text.secondary",
    width: "18px",
    height: "18px",
  },
});

export type PageHeaderProps = Readonly<{
  header: string | string[] | React.ReactNode;
  subheader?: React.ReactNode;
  belowTitle?: React.ReactNode;
  icon?: React.ReactNode;
  headerComponents?: React.ReactNode;
  backButtonUrl?: string;
  backButtonAction?: () => void;
  backButtonState?: any;
  backButtonAriaLabel?: string;
  backButtonSize?: "small" | "medium" | "large";
  paddingBottom?: number | string;
}>;

export default function PageHeader({
  header,
  subheader = "",
  belowTitle,
  icon,
  headerComponents,
  backButtonUrl,
  backButtonAction,
  backButtonState,
  backButtonAriaLabel,
  backButtonSize = "large",
  paddingBottom = 3,
}: PageHeaderProps) {
  const classes = css();

  const headerComponent = (
    headerValue: string | string[] | React.ReactNode,
  ): React.ReactNode => {
    if (typeof headerValue === "string") {
      return <Box component="span">{headerValue}</Box>;
    }
    if (Array.isArray(headerValue)) {
      return headerValue.map((element, idx) => (
        <Box key={element}>
          {element}
          {idx < headerValue.length - 1 && (
            <ChevronRightIcon sx={classes.headerChevron} />
          )}
        </Box>
      ));
    }
    return headerValue;
  };

  return (
    <Grid
      container
      justifyContent="space-between"
      columnGap={5}
      rowGap={2}
      paddingBottom={paddingBottom}
    >
      <Grid
        container
        size={{ xs: 12, md: "grow" }}
        minHeight={6}
        color="text.secondary"
        alignItems={backButtonSize === "small" ? "center" : undefined}
      >
        <Grid size={{ xs: 12, md: "auto" }}>
          {backButtonUrl && (
            <IconButton
              color="inherit"
              component={NavLink}
              to={backButtonUrl}
              state={backButtonState}
              aria-label={backButtonAriaLabel}
              size={backButtonSize}
              sx={{ mr: backButtonSize === "small" ? 2 : 0 }}
            >
              <ChevronLeftIcon />
            </IconButton>
          )}
          {backButtonAction && (
            <IconButton
              color="inherit"
              onClick={backButtonAction}
              aria-label={backButtonAriaLabel}
              size={backButtonSize}
              sx={{ mr: backButtonSize === "small" ? 2 : 0 }}
            >
              <ChevronLeftIcon />
            </IconButton>
          )}
        </Grid>
        <Grid
          container
          size={{ xs: 12, md: "grow" }}
          alignItems="center"
          wrap="nowrap"
          gap={2}
          sx={{ minWidth: 0 }}
        >
          {icon && <Grid size="auto">{icon}</Grid>}
          <Grid size="grow" sx={{ minWidth: 0 }}>
            <Typography component="div" variant="h6" color="text.primary">
              <Box display="flex" alignItems="center" gap={1}>
                {headerComponent(header)}
              </Box>
            </Typography>
            {belowTitle}
            {subheader && (
              <Typography component="div" variant="body2">
                {subheader}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Grid>
      <Grid size="auto" marginLeft="auto" alignSelf="center">
        <Box>{headerComponents}</Box>
      </Grid>
    </Grid>
  );
}
